// Single source of truth for the Admin section's destinations. Used by BOTH the
// header "Admin" dropdown (AuthenticatedDashboard) and the Admin sidebar
// (AdminLayout) so they never drift apart. All routes are mounted in index.js
// under the AdminLayout wrapper.
//
// Rebuilt against the eastview-os `/admin/*` routers (the frontend_documentation
// specs for each are named per entry). The previous entries — Pricing / Grids /
// Rules / Sizer / DSCR / Task Lists / Guidelines — were digital_sizer ports
// pointing at routes eastview-os never exposed (see the "TBD (no eastview-os
// route)" markers in src/api/adminPricingApi.js and src/api/guidelinesApi.js);
// every call 404'd. Those screens are still mounted at their old paths for
// direct-URL access but are out of the Admin nav.
//
// `group` buckets the item in the sidebar; the order of first appearance is the
// order the groups render in. `icon` is the sidebar glyph (and the rail's only
// affordance when collapsed, so every item needs one).
import BlockRounded from "@mui/icons-material/BlockRounded";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import HubOutlined from "@mui/icons-material/HubOutlined";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import ShowChartRounded from "@mui/icons-material/ShowChartRounded";
import StorageRounded from "@mui/icons-material/StorageRounded";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";

export const ADMIN_GROUPS = {
  policy: "Pricing & policy",
  pipeline: "Data pipeline",
  oversight: "Oversight",
};

export const ADMIN_NAV_ITEMS = [
  // /admin/data/guidelines/* — guidelines-and-dscr-pricing-admin.md
  {
    to: "/admin/guidelines",
    label: "Guidelines",
    group: ADMIN_GROUPS.policy,
    icon: MenuBookOutlined,
  },
  // One screen, three tabs — /admin/pricing/{dscr,rtl,config}. Consolidates the
  // former DSCR Pricing / RTL Pricing / Pricing Config entries, which split one
  // job across three destinations. Covers:
  //   /admin/data/dscr-pricing/* — guidelines-and-dscr-pricing-admin.md
  //   /admin/data/rtl-pricing/*  — the additive RTL/GUC rate model
  //   /correspondents/admin/{tier_margins,rtl_base_rates} —
  //     pricing-config-and-lock-extensions.md §1–§2
  {
    to: "/admin/pricing",
    label: "Pricing",
    group: ADMIN_GROUPS.policy,
    icon: AttachMoneyRounded,
    // `subTabs` render as AdminLayout's sticky switcher under the breadcrumb.
    // Only Pricing has them today; any screen can.
    subTabs: [
      { to: "/admin/pricing/dscr", label: "DSCR Pricing" },
      { to: "/admin/pricing/rtl", label: "RTL & GUC Pricing" },
      { to: "/admin/pricing/config", label: "Pricing Config" },
    ],
  },
  // (Sizers used to sit here at /admin/sizers; they're their own top-level
  // section now — see sizersNav.js.)
  // /admin/data/zhvi/* — admin-market-data-zhvi.md
  {
    to: "/admin/market-data",
    label: "Market Data",
    group: ADMIN_GROUPS.policy,
    icon: ShowChartRounded,
  },
  // /admin/schemas/*
  {
    to: "/admin/schemas",
    label: "Schemas",
    group: ADMIN_GROUPS.pipeline,
    icon: StorageRounded,
  },
  // /admin/extractors (read-only)
  {
    to: "/admin/extractors",
    label: "Extractors",
    group: ADMIN_GROUPS.pipeline,
    icon: FileDownloadOutlined,
  },
  // /admin/graph-subscriptions
  {
    to: "/admin/graph-subscriptions",
    label: "Graph Subs",
    group: ADMIN_GROUPS.pipeline,
    icon: HubOutlined,
  },
  // /admin/data/banned-appraisers — banned-appraisers.md (moved off
  // /loan-processing 2026-08-13; only the workflow `check` stayed). NOT one of the
  // descoped digital_sizer ports above: eastview-os serves this router, and the
  // appraisal-review flag reads the same list. It was swept out of the nav with
  // that batch by mistake.
  {
    to: "/admin/banned-appraisers",
    label: "Banned Appraisers",
    group: ADMIN_GROUPS.oversight,
    icon: BlockRounded,
  },
  // NOTE: User Activity used to live here. It moved to the Users screen as its
  // "Activity" tab — it is a question about the people on that screen, and it
  // is gated by membership of the "User Activity" org group rather than by the
  // admin role this menu is built on. See screens/UsersScreen/UserActivity/.
];

/** The nav in render order, bucketed by `group` (groups keep first-seen order). */
export const adminNavGroups = () => {
  const out = [];
  ADMIN_NAV_ITEMS.forEach((item) => {
    const key = item.group || ADMIN_GROUPS.policy;
    const existing = out.find((g) => g.label === key);
    if (existing) existing.items.push(item);
    else out.push({ label: key, items: [item] });
  });
  return out;
};
